import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { shopLabels } from '../../data/shopLabels';
import { ease } from '../../core/format/motion';
import { LAYERS } from '../../ui/layers';
import { pushModal, removeModal, type ModalEntry } from './modalStack';
type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
};
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/** Wacht tot de lade in beeld schuift voordat de focus erheen gaat. */
const FOCUS_DELAY_MS = 60;

/**
 * Zijlade (winkelmand, afspraak, huidadvies, vraag aan Sarah).
 * Toegankelijk als modaal venster: titel via aria-labelledby, focus blijft in de lade,
 * Escape sluit, en de rest van de pagina is `inert` (niet bereikbaar met Tab of schermlezer).
 * Bij sluiten gaat de focus terug naar de knop waarmee hij geopend werd.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  footer
}: Props) {
  const reduced = useReducedMotion();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [depth, setDepth] = useState(0);
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), FOCUS_DELAY_MS);

    // Scroll-lock, `inert` en toetsen via de gedeelde stapel: alleen de bovenste lade luistert.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    const entry: ModalEntry = {
      root: () => rootRef.current,
      onKey
    };
    setDepth(pushModal(entry));
    return () => {
      window.clearTimeout(focusTimer);
      removeModal(entry);
      // Alleen terug naar de knop als die nog bestaat en niet zelf in een (andere) lade onbereikbaar is.
      if (previouslyFocused?.isConnected && !previouslyFocused.closest('[inert]')) previouslyFocused.focus();
    };
  }, [open]);
  if (typeof document === 'undefined') return null;
  return createPortal(<AnimatePresence data-id="element-8613" data-mp="1h5ryvk4yft4g">
      {open && <div ref={rootRef} className="fixed inset-0 z-overlay overflow-hidden"
    // Een later geopende lade ligt altijd boven een eerdere, ook als die nog wegschuift. Blijft onder de meldingen.
    style={depth > 0 ? {
      zIndex: Math.min(LAYERS.overlay + depth, LAYERS.toast - 1)
    } : undefined} role="dialog" aria-modal="true" aria-labelledby={titleId} data-id="element-8614" data-mp="1h2dy2gbxvrz4">
          <motion.button type="button" tabIndex={-1} aria-hidden="true" onClick={onClose} className="absolute inset-0 h-full w-full cursor-default bg-ink/30 backdrop-blur-[3px]" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} data-id="element-8615" data-mp="1uwf2nqpko97e" />
          <motion.aside ref={panelRef as RefObject<HTMLElement>} className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-shell shadow-2xl" initial={reduced ? {
        opacity: 0
      } : {
        x: '100%'
      }} animate={reduced ? {
        opacity: 1
      } : {
        x: 0
      }} exit={reduced ? {
        opacity: 0
      } : {
        x: '100%'
      }} transition={{
        duration: 0.28,
        ease
      }} data-id="element-8616" data-mp="2duhibew8ctsj">
            <header className="flex items-center justify-between gap-4 border-b border-line px-6 py-5" data-id="element-8617" data-mp="1teogyu565hu1">
              <h2 id={titleId} className="font-display text-xl text-ink" data-id="element-8618" data-mp="2h4simvysr7ie">
                {title}
              </h2>
              <button ref={closeRef} type="button" onClick={onClose} aria-label={`${shopLabels.close}: ${title}`} className="-mr-2.5 rounded-full p-2.5 text-muted transition-colors duration-150 ease-soft hover:bg-sand hover:text-ink" data-id="element-8619" data-mp="2m1sp37zk6ca1">
                <XIcon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" data-id="element-8620" data-mp="34t20xnwsxd4s" />
              </button>
            </header>
            <motion.div className="flex-1 overflow-y-auto overscroll-contain px-6 py-6" initial={reduced ? false : {
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.24,
          delay: reduced ? 0 : 0.08,
          ease
        }} data-id="element-8621" data-mp="tdba9itzzmpu">
              {children}
            </motion.div>
            {footer && <div className="border-t border-line bg-shell px-6 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5" data-id="element-8622" data-mp="y75s0gq1lrma">{footer}</div>}
          </motion.aside>
        </div>}
    </AnimatePresence>, document.body);
}